import { toCanonicalCitations } from "@/components/MedicalChronology/Visits/utils/visitCitations";
import type { MedVisit } from "@/types/medchron";

import type { MatchVisitsResult, MatchVisitsWithModeResult } from "./types";
import { CompareVersionsConstants, VisitMatchMode } from "./types";

export const matchVisitsById = (
  oldVisits: MedVisit[],
  newVisits: MedVisit[]
): MatchVisitsResult => {
  const oldById = new Map(oldVisits.map((v) => [v.visit_id, v]));
  const newById = new Map(newVisits.map((v) => [v.visit_id, v]));

  const removed = oldVisits.filter((v) => !newById.has(v.visit_id));
  const added = newVisits.filter((v) => !oldById.has(v.visit_id));
  const matched: Array<[MedVisit, MedVisit]> = [];
  for (const oldVisit of oldVisits) {
    const newVisit = newById.get(oldVisit.visit_id);
    if (newVisit) matched.push([oldVisit, newVisit]);
  }

  return { removed, added, matched };
};

const buildVisitContentKey = (visit: MedVisit): string =>
  [
    visit.visit_date ?? "",
    visit.visit_type_id,
    ...toCanonicalCitations(visit).map(
      (citation) => `${citation.file_id}:${citation.start_page}-${citation.end_page}`
    ),
  ].join("|");

const tokenizeVisitDetails = (visit: MedVisit): Set<string> =>
  new Set(
    visit.med_visit_details
      .flatMap((detail) => detail.visit_details.toLowerCase().split(/[^a-z0-9]+/))
      .filter((token) => token !== "")
  );

const computeTokenSimilarity = (oldTokens: Set<string>, newTokens: Set<string>): number => {
  if (oldTokens.size === 0 && newTokens.size === 0) return 1;
  let sharedCount = 0;
  oldTokens.forEach((token) => {
    if (newTokens.has(token)) sharedCount += 1;
  });
  return (2 * sharedCount) / (oldTokens.size + newTokens.size);
};

const normalizeName = (name: string | null | undefined): string =>
  (name ?? "").trim().toLowerCase();

const countMatchingProviderFields = (oldVisit: MedVisit, newVisit: MedVisit): number =>
  Number(normalizeName(oldVisit.provider_name) === normalizeName(newVisit.provider_name)) +
  Number(normalizeName(oldVisit.facility_name) === normalizeName(newVisit.facility_name));

const groupIndicesByContentKey = (visits: MedVisit[]): Map<string, number[]> => {
  const indicesByKey = new Map<string, number[]>();
  visits.forEach((visit, index) => {
    const key = buildVisitContentKey(visit);
    const indices = indicesByKey.get(key);
    if (indices) indices.push(index);
    else indicesByKey.set(key, [index]);
  });
  return indicesByKey;
};

interface ContentPairCandidate {
  oldIndex: number;
  newIndex: number;
  similarity: number;
  matchingProviderFieldCount: number;
}

interface TokenizedVisits {
  visits: MedVisit[];
  tokens: Set<string>[];
}

const tokenizeVisits = (visits: MedVisit[]): TokenizedVisits => ({
  visits,
  tokens: visits.map(tokenizeVisitDetails),
});

/**
 * Scores every old/new pair in one content-key group, best pair first.
 * - Provider and facility only break ties: they vary between pipeline runs.
 */
const rankContentPairCandidates = (
  oldIndices: number[],
  newIndices: number[],
  oldSide: TokenizedVisits,
  newSide: TokenizedVisits
): ContentPairCandidate[] =>
  oldIndices
    .flatMap((oldIndex) =>
      newIndices.map(
        (newIndex): ContentPairCandidate => ({
          oldIndex,
          newIndex,
          similarity: computeTokenSimilarity(oldSide.tokens[oldIndex], newSide.tokens[newIndex]),
          matchingProviderFieldCount: countMatchingProviderFields(
            oldSide.visits[oldIndex],
            newSide.visits[newIndex]
          ),
        })
      )
    )
    .sort(
      (left, right) =>
        right.similarity - left.similarity ||
        right.matchingProviderFieldCount - left.matchingProviderFieldCount ||
        left.oldIndex - right.oldIndex ||
        left.newIndex - right.newIndex
    );

/**
 * Greedily pairs ranked candidates of one key group, returning new index by old index.
 * - Equal-sized groups pair fully; unequal ones drop pairs below CONTENT_MATCH_MIN_SIMILARITY.
 * - Equal counts trust the shared date, type and pages over text, so a rewritten visit still pairs.
 */
const assignContentPairs = (
  oldIndices: number[],
  newIndices: number[],
  rankedCandidates: ContentPairCandidate[]
): Map<number, number> => {
  const minSimilarity =
    oldIndices.length === newIndices.length
      ? 0
      : CompareVersionsConstants.CONTENT_MATCH_MIN_SIMILARITY;
  const newIndexByOldIndex = new Map<number, number>();
  const pairedNewIndices = new Set<number>();
  for (const candidate of rankedCandidates) {
    if (
      candidate.similarity < minSimilarity ||
      newIndexByOldIndex.has(candidate.oldIndex) ||
      pairedNewIndices.has(candidate.newIndex)
    ) {
      continue;
    }
    newIndexByOldIndex.set(candidate.oldIndex, candidate.newIndex);
    pairedNewIndices.add(candidate.newIndex);
  }
  return newIndexByOldIndex;
};

const buildCitedPageSet = (visit: MedVisit): Set<string> => {
  const pages = new Set<string>();
  toCanonicalCitations(visit).forEach((citation) => {
    for (let page = citation.start_page; page <= citation.end_page; page += 1) {
      pages.add(`${citation.file_id}:${page}`);
    }
  });
  return pages;
};

const computePageSimilarity = (oldPages: Set<string>, newPages: Set<string>): number => {
  if (oldPages.size === 0 || newPages.size === 0) return 0;
  let sharedCount = 0;
  oldPages.forEach((page) => {
    if (newPages.has(page)) sharedCount += 1;
  });
  const unionSize = oldPages.size + newPages.size - sharedCount;
  const smallerSize = Math.min(oldPages.size, newPages.size);
  return (sharedCount / unionSize + sharedCount / smallerSize) / 2;
};

interface ScoredPairCandidate {
  oldIndex: number;
  newIndex: number;
  score: number;
}

/**
 * Scores same-date visits left over after key matching, best pair first.
 * - Page overlap tolerates ranges that grew, shrank, or split between runs; type and provider are weak signals.
 * - Detail-text similarity must clear a floor so shared pages alone never pair unrelated visits.
 */
const rankScoredPairCandidates = (
  unpairedOldIndices: number[],
  unpairedNewIndices: number[],
  oldSide: TokenizedVisits,
  newSide: TokenizedVisits
): ScoredPairCandidate[] => {
  const oldPages = unpairedOldIndices.map((index) => buildCitedPageSet(oldSide.visits[index]));
  const newPages = unpairedNewIndices.map((index) => buildCitedPageSet(newSide.visits[index]));
  const candidates: ScoredPairCandidate[] = [];

  unpairedOldIndices.forEach((oldIndex, oldPosition) => {
    const oldVisit = oldSide.visits[oldIndex];
    unpairedNewIndices.forEach((newIndex, newPosition) => {
      const newVisit = newSide.visits[newIndex];
      if ((oldVisit.visit_date ?? "") !== (newVisit.visit_date ?? "")) return;
      if (oldSide.tokens[oldIndex].size === 0 || newSide.tokens[newIndex].size === 0) return;
      const pageSimilarity = computePageSimilarity(oldPages[oldPosition], newPages[newPosition]);
      const textSimilarity = computeTokenSimilarity(
        oldSide.tokens[oldIndex],
        newSide.tokens[newIndex]
      );
      const minTextSimilarity =
        buildVisitContentKey(oldVisit) === buildVisitContentKey(newVisit)
          ? CompareVersionsConstants.CONTENT_MATCH_MIN_SIMILARITY
          : CompareVersionsConstants.SCORED_MATCH_MIN_TEXT_SIMILARITY;
      if (pageSimilarity === 0 || textSimilarity < minTextSimilarity) return;
      const score =
        CompareVersionsConstants.SCORED_MATCH_PAGE_WEIGHT * pageSimilarity +
        CompareVersionsConstants.SCORED_MATCH_TEXT_WEIGHT * textSimilarity +
        CompareVersionsConstants.SCORED_MATCH_TYPE_WEIGHT *
          Number(oldVisit.visit_type_id === newVisit.visit_type_id) +
        (CompareVersionsConstants.SCORED_MATCH_PROVIDER_WEIGHT *
          countMatchingProviderFields(oldVisit, newVisit)) /
          CompareVersionsConstants.PROVIDER_FIELD_COUNT;
      if (score >= CompareVersionsConstants.SCORED_MATCH_MIN_SCORE) {
        candidates.push({ oldIndex, newIndex, score });
      }
    });
  });

  return candidates.sort(
    (left, right) =>
      right.score - left.score || left.oldIndex - right.oldIndex || left.newIndex - right.newIndex
  );
};

const pairLeftoversByScore = (
  oldSide: TokenizedVisits,
  newSide: TokenizedVisits,
  newIndexByOldIndex: Map<number, number>
): void => {
  const pairedNewIndices = new Set(newIndexByOldIndex.values());
  const unpairedOldIndices = oldSide.visits
    .map((_, index) => index)
    .filter((index) => !newIndexByOldIndex.has(index));
  const unpairedNewIndices = newSide.visits
    .map((_, index) => index)
    .filter((index) => !pairedNewIndices.has(index));

  for (const candidate of rankScoredPairCandidates(
    unpairedOldIndices,
    unpairedNewIndices,
    oldSide,
    newSide
  )) {
    if (newIndexByOldIndex.has(candidate.oldIndex) || pairedNewIndices.has(candidate.newIndex)) {
      continue;
    }
    newIndexByOldIndex.set(candidate.oldIndex, candidate.newIndex);
    pairedNewIndices.add(candidate.newIndex);
  }
};

/**
 * Pairs visits by exact date, type and cited pages, then pairs the leftovers by weighted similarity.
 * - Independent runs shift page ranges and types, so exact keys alone leave visits unpaired.
 */
export const matchVisitsByContent = (
  oldVisits: MedVisit[],
  newVisits: MedVisit[]
): MatchVisitsResult => {
  const oldSide = tokenizeVisits(oldVisits);
  const newSide = tokenizeVisits(newVisits);
  const newIndicesByKey = groupIndicesByContentKey(newVisits);
  const newIndexByOldIndex = new Map<number, number>();

  groupIndicesByContentKey(oldVisits).forEach((oldIndices, key) => {
    const newIndices = newIndicesByKey.get(key);
    if (!newIndices) return;
    const rankedCandidates = rankContentPairCandidates(oldIndices, newIndices, oldSide, newSide);
    assignContentPairs(oldIndices, newIndices, rankedCandidates).forEach((newIndex, oldIndex) =>
      newIndexByOldIndex.set(oldIndex, newIndex)
    );
  });

  pairLeftoversByScore(oldSide, newSide, newIndexByOldIndex);

  const pairedNewIndices = new Set(newIndexByOldIndex.values());
  const matched: Array<[MedVisit, MedVisit]> = [];
  const removed: MedVisit[] = [];
  oldVisits.forEach((oldVisit, oldIndex) => {
    const newIndex = newIndexByOldIndex.get(oldIndex);
    if (newIndex === undefined) removed.push(oldVisit);
    else matched.push([oldVisit, newVisits[newIndex]]);
  });
  const added = newVisits.filter((_, newIndex) => !pairedNewIndices.has(newIndex));

  return { removed, added, matched };
};

const selectVisitMatchMode = (
  sharedIdCount: number,
  smallerVersionCount: number
): VisitMatchMode =>
  smallerVersionCount === 0 ||
  sharedIdCount / smallerVersionCount >= CompareVersionsConstants.ID_MATCH_MIN_OVERLAP_RATIO
    ? VisitMatchMode.Id
    : VisitMatchMode.Content;

/**
 * Pairs by visit_id when versions share enough IDs (copied versions), else by content (independent runs).
 * - Content mode still pairs the few shared IDs first, which keeps every entry's visitId unique.
 */
export const matchVisits = (
  oldVisits: MedVisit[],
  newVisits: MedVisit[]
): MatchVisitsWithModeResult => {
  const idMatch = matchVisitsById(oldVisits, newVisits);
  const smallerVersionCount = Math.min(oldVisits.length, newVisits.length);
  if (selectVisitMatchMode(idMatch.matched.length, smallerVersionCount) === VisitMatchMode.Id) {
    return { ...idMatch, matchMode: VisitMatchMode.Id };
  }
  const contentMatch = matchVisitsByContent(idMatch.removed, idMatch.added);
  return {
    removed: contentMatch.removed,
    added: contentMatch.added,
    matched: [...idMatch.matched, ...contentMatch.matched],
    matchMode: VisitMatchMode.Content,
  };
};
